import { Animated, StyleSheet } from 'react-native'

import { useTheme } from '../theme/restyle'
import {
  birthDateRange,
  parseCalendarDate,
  toCalendarDate,
} from './calendarDate'
import { KYC_FORM_METRICS } from './kycFormTheme'
import {
  DATE_PICKER_DISPLAY,
  DATE_PICKER_IS_INLINE,
  type NativeDatePicker,
} from './nativeDatePicker'

/**
 * The date picker in the keyboard's slot (1844:17543).
 *
 * iOS draws its calendar inline, so the panel is a surface the step gives up
 * room for. Android opens a dialog over everything instead — the same
 * component, mounted with nothing reserved for it.
 */
export function FiatDatePickerDock({
  Picker,
  value,
  isDark,
  isOpen,
  slide,
  onChangeValue,
  onDismiss,
}: {
  Picker: NativeDatePicker
  /** The answer held so far, in the wire format. */
  value: string
  isDark: boolean
  isOpen: boolean
  /** 0 closed, 1 open — what slides the panel in off the bottom edge. */
  slide: Animated.AnimatedValue
  onChangeValue: (value: string) => void
  /**
   * The user closed the picker without choosing. Android's dialog closes
   * itself, and the module re-shows a dialog that is still mounted on the next
   * render — so the holder has to hear about it and unmount.
   */
  onDismiss?: () => void
}) {
  const theme = useTheme()
  const { minimumDate, maximumDate } = birthDateRange()
  // An unanswered field opens on the latest date offered rather than today's
  // clock, so the picker never opens on a date it would refuse.
  const selected = parseCalendarDate(value) ?? maximumDate

  const picker = (
    <Picker
      value={selected}
      mode="date"
      display={DATE_PICKER_DISPLAY}
      minimumDate={minimumDate}
      maximumDate={maximumDate}
      // The host's theme is pinned, so the OS setting is not what decides
      // whether the calendar is drawn light or dark.
      themeVariant={isDark ? 'dark' : 'light'}
      onChange={(event, date) => {
        if (event.type === 'dismissed') {
          onDismiss?.()
          return
        }
        if (date !== undefined) {
          onChangeValue(toCalendarDate(date))
        }
      }}
    />
  )

  // A dialog is the platform's to animate, and mounting it is what opens it.
  if (!DATE_PICKER_IS_INLINE) {
    return isOpen ? picker : null
  }

  return (
    <Animated.View
      // Mounted while closed so it has somewhere to slide from, and left out
      // of the touch tree there so it cannot take a press through the page.
      pointerEvents={isOpen ? 'auto' : 'none'}
      style={[
        styles.dock,
        { backgroundColor: theme.background.modal },
        {
          transform: [
            {
              translateY: slide.interpolate({
                inputRange: [0, 1],
                outputRange: [KYC_FORM_METRICS.datePickerHeight, 0],
              }),
            },
          ],
        },
      ]}
    >
      {picker}
    </Animated.View>
  )
}

const styles = StyleSheet.create({
  dock: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: KYC_FORM_METRICS.datePickerHeight,
    paddingHorizontal: KYC_FORM_METRICS.datePickerInsetX,
    paddingTop: KYC_FORM_METRICS.datePickerInsetY,
  },
})
